import { type PropsWithChildren, useMemo } from "react";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { createApiClient, createExonnApi } from "@exonn/js-sdk";
import { isInIframe, isPlainObject } from "@exonn/utils";
import { setExonnApi } from "src/exonn-api/exonn-api.tsx";
import { ExonnApiContext } from "src/exonn-api/ExonnApiContext.ts";
import type { InternalAxiosRequestConfig } from "axios";
import { uid } from "uid/single";
import { appStore } from "src/stores/app.store.ts";

const XDEBUG_TRIGGER_QUERY_PARAM = "XDEBUG_SESSION_START";

const addParameter = (
  config: InternalAxiosRequestConfig,
  key: string,
  value: any,
) => {
  const { method } = config;
  const type = method === "post" ? "data" : "params";

  // config['data'] can contain string value
  // to prevent destructuring a string we will check if config['data'] is the object
  if (isPlainObject(config[type]) || config[type] === undefined) {
    config[type] = {
      [key]: value,
      ...config[type],
    };
  }

  if (config["data"] instanceof FormData) {
    config["data"].append(key, value + "");
  }
  return config;
};

export const ExonnApiProvider = ({ children }: PropsWithChildren) => {
  const { apiBaseUrl } = useClientConfig();

  const api = useMemo(() => {
    const client = createApiClient(
      {
        serverUrl: apiBaseUrl,
      },
      {
        withCredentials: true,
        withXSRFToken: true,
      },
    );
    client.interceptors.request.use((config) => {
      const token = localStorage.getItem("authToken");

      // we use cookies to authenticate outside iframe
      if (token && isInIframe()) {
        config.headers.set("Authorization", "Bearer " + token);
      }
      const debugQueries = appStore.debugDbQueries;
      if (debugQueries) {
        addParameter(config, "sql-debug", true);
      }

      if (import.meta.env.DEV) {
        addParameter(config, XDEBUG_TRIGGER_QUERY_PARAM, 1);
      }

      config.headers.set("X-Request-id", uid());
      config.headers.set(
        "X-Ui-Locale",
        localStorage.getItem("language") ?? "de",
      );

      return config;
    });
    return createExonnApi(client);
  }, [apiBaseUrl]);

  setExonnApi(api);

  return (
    <ExonnApiContext.Provider value={api}>{children}</ExonnApiContext.Provider>
  );
};
